import React from "react";
import * as styles from "./Footer.module.css";
import { Link } from "../../elements/Link/Link";
import { NavigationItem, PageLink } from "../../../types";
import { ImageItem } from "@kontent-ai/gatsby-components/dist/image-element";
import CookieBanner from "../../blocks/CookieBanner";

interface Props {
  id: string;
  secondaryNavigation?: PageLink[];
  navigation?: NavigationItem[];
  images?: {
    item: ImageItem;
    alt: string;
  }[];
  socialIcons?: {
    link: PageLink;
    image: {
      item: ImageItem;
      alt: string;
    };
  }[];
  style: "dark" | "light" | "transparent";
  copyright?: string;
}

export const Footer: React.FC<Props> = ({
  style = "light",
  id,
  secondaryNavigation,
  navigation,
  images,
  socialIcons,
  copyright,
}) => {
  const containerStyles = [styles.footer];
  style === "dark" && containerStyles.push(styles.dark);
  style === "transparent" && containerStyles.push(styles.transparent);

  return (
    <footer
      className={containerStyles.join(" ")}
      id="site-footer"
      data-kontent-element-codename="footer"
      data-kontent-item-id={id}
    >
      <div className={styles.content}>
        {navigation && navigation?.length > 0 && (
          <ul className={styles.linkColumns}>
            {navigation?.map((item, idx) => {
              return (
                <li className={styles.linkColumn} key={item.link?.id}>
                  <Link key={`footerPrim-${idx}`} to={item.link.href}>
                    {item.title}
                  </Link>
                  {item?.subnavigation && item?.subnavigation?.length > 0 && (
                    <ul>
                      {item.subnavigation?.map((subItem, subIdx) => {
                        return (
                          <li key={subItem.link?.id}>
                            {subItem?.link?.href ? (
                              <Link
                                key={`footerPrim-${idx}-sub-${subIdx}`}
                                to={subItem?.link?.href}
                              >
                                {subItem?.title}
                              </Link>
                            ) : (
                              subItem?.title
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {images && images?.length > 0 && (
          <div className={styles.images}>
            {images.map((image, idx) => {
              return (
                <img
                  src={image?.item.url}
                  alt={image?.alt}
                  key={`image-${idx}`}
                  height="90"
                  width="230"
                />
              );
            })}
          </div>
        )}
        <div className={styles.bottom}>
          {secondaryNavigation && secondaryNavigation?.length > 0 && (
            <div className={styles.secondaryNav}>
              {secondaryNavigation?.map((item, idx) => {
                return (
                  <Link
                    to={item.href}
                    target={item.target}
                    key={`footerSecond-${idx}`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </div>
          )}
          {copyright && <div className={styles.copyright}>{copyright}</div>}
          {socialIcons && socialIcons?.length > 0 && (
            <div className={styles.socialIcons}>
              {socialIcons.map((icon, idx) => {
                return (
                  <Link
                    to={icon.link.href}
                    target={icon.link.target}
                    key={icon?.link?.id}
                  >
                    <img src={icon.image.item?.url} alt={icon.image.alt} />
                    <div className="vis-hidden">{icon.link?.label}</div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <CookieBanner />
    </footer>
  );
};
